import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  Settings, Save, Loader2, FileText, Home, Clock, DollarSign, Users,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    property_name: '',
    property_address: '',
    check_in_time: '',
    check_out_time: '',
    security_deposit: 0,
    max_occupancy: 14,
    payment_email: '',
    payment_password: '',
    terms_and_conditions: ''
  });

  // TipTap Editor for Terms & Conditions
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapUnderline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: settings.terms_and_conditions,
    onUpdate: ({ editor }) => {
      setSettings(prev => ({ ...prev, terms_and_conditions: editor.getHTML() }));
    },
  });

  // Update editor content when settings load
  useEffect(() => {
    if (editor && settings.terms_and_conditions && editor.getHTML() !== settings.terms_and_conditions) {
      editor.commands.setContent(settings.terms_and_conditions);
    }
  }, [settings.terms_and_conditions, editor]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/contract-settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contract-settings`, settings);
      toast.success('Contract settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1B4D3E]">Contract Settings</h1>
          <p className="text-gray-500">Customize contract content and terms</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Property Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5" /> Property Information
            </CardTitle>
            <CardDescription>Basic property details shown on contracts</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="property_name">Property Name</Label>
              <Input
                id="property_name"
                value={settings.property_name}
                onChange={(e) => handleChange('property_name', e.target.value)}
                placeholder="Big Bear"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="property_address">Property Address</Label>
              <Input
                id="property_address"
                value={settings.property_address}
                onChange={(e) => handleChange('property_address', e.target.value)}
                placeholder="119 Newtons Road, Jack Lake, Ontario"
              />
            </div>
          </CardContent>
        </Card>

        {/* Check-in/out Times */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" /> Check-in & Check-out
            </CardTitle>
            <CardDescription>Default times for rentals</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="check_in_time">Check-in Time</Label>
              <Input
                id="check_in_time"
                value={settings.check_in_time}
                onChange={(e) => handleChange('check_in_time', e.target.value)}
                placeholder="4:30 PM"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="check_out_time">Check-out Time</Label>
              <Input
                id="check_out_time"
                value={settings.check_out_time}
                onChange={(e) => handleChange('check_out_time', e.target.value)}
                placeholder="11:00 AM"
              />
            </div>
          </CardContent>
        </Card>

        {/* Financial Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Financial Settings
            </CardTitle>
            <CardDescription>Security deposit and payment information</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="security_deposit">Security Deposit ($)</Label>
              <Input
                id="security_deposit"
                type="number"
                value={settings.security_deposit}
                onChange={(e) => handleChange('security_deposit', parseFloat(e.target.value) || 0)}
                placeholder="2500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_occupancy">Maximum Occupancy</Label>
              <Input
                id="max_occupancy"
                type="number"
                value={settings.max_occupancy}
                onChange={(e) => handleChange('max_occupancy', parseInt(e.target.value) || 14)}
                placeholder="14"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_email">E-Transfer Email</Label>
              <Input
                id="payment_email"
                type="email"
                value={settings.payment_email}
                onChange={(e) => handleChange('payment_email', e.target.value)}
                placeholder="neill@neilldixon.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_password">E-Transfer Password</Label>
              <Input
                id="payment_password"
                value={settings.payment_password}
                onChange={(e) => handleChange('payment_password', e.target.value)}
                placeholder="bigbear"
              />
            </div>
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Terms & Conditions
            </CardTitle>
            <CardDescription>
              Contract terms shown to renters. Use the formatting toolbar to style your content.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Rich Text Editor Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-[#F9F8F6] border border-[#E5E0DB] rounded-t-md">
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleBold().run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('bold') ? 'bg-[#E5E0DB]' : ''}`}
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('italic') ? 'bg-[#E5E0DB]' : ''}`}
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('underline') ? 'bg-[#E5E0DB]' : ''}`}
                title="Underline"
              >
                <Underline className="w-4 h-4" />
              </button>
              
              <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
              
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('bulletList') ? 'bg-[#E5E0DB]' : ''}`}
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('orderedList') ? 'bg-[#E5E0DB]' : ''}`}
                title="Numbered List"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
              
              <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
              
              <button
                type="button"
                onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive({ textAlign: 'left' }) ? 'bg-[#E5E0DB]' : ''}`}
                title="Align Left"
              >
                <AlignLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive({ textAlign: 'center' }) ? 'bg-[#E5E0DB]' : ''}`}
                title="Align Center"
              >
                <AlignCenter className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive({ textAlign: 'right' }) ? 'bg-[#E5E0DB]' : ''}`}
                title="Align Right"
              >
                <AlignRight className="w-4 h-4" />
              </button>
              
              <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
              
              {/* Heading buttons */}
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`px-2 py-1 rounded hover:bg-[#E5E0DB] text-sm font-semibold ${editor?.isActive('heading', { level: 2 }) ? 'bg-[#E5E0DB]' : ''}`}
                title="Heading"
              >
                H2
              </button>
              <button
                type="button"
                onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                className={`px-2 py-1 rounded hover:bg-[#E5E0DB] text-sm font-semibold ${editor?.isActive('heading', { level: 3 }) ? 'bg-[#E5E0DB]' : ''}`}
                title="Subheading"
              >
                H3
              </button>
            </div>
            
            {/* Editor Content */}
            <div className="border border-t-0 border-[#E5E0DB] rounded-b-md min-h-[400px] prose prose-sm max-w-none bg-white">
              <EditorContent 
                editor={editor} 
                className="p-4 min-h-[400px] focus:outline-none [&_.ProseMirror]:min-h-[380px] [&_.ProseMirror]:outline-none"
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Use the toolbar to format your terms and conditions with headings, lists, and text styling.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
